// app/(app)/reports/payments.tsx
import { Button } from "@/components/default/forms";
import DatePicker from "@/components/default/forms/DatePicker";
import SearchableSelect from "@/components/default/forms/SearchableSelect";
import { useAppTheme } from "@/hooks/useAppTheme";
import { locationApi } from "@/services/locationApi";
import {
  PaymentReportItem,
  PaymentReportSummary,
  reportsApi,
} from "@/services/reportsApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchCustomers } from "@/store/slices/customerSlice";
import { fetchEmployees } from "@/store/slices/employeeSlice";
import { showAlert } from "@/utils/alert";
import { Ionicons } from "@expo/vector-icons";
import * as FileSystem from "expo-file-system/legacy";
import { useRouter } from "expo-router";
import * as Sharing from "expo-sharing";
import { useEffect, useState } from "react";
import {
  FlatList,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

export default function PaymentReportScreen() {
  const { colors, spacing } = useAppTheme();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { customers, loading: customersLoading } = useAppSelector(
    (state) => state.customers,
  );
  const { employees, loading: employeesLoading } = useAppSelector(
    (state) => state.employees,
  );

  const isEmployee = user?.userType === "Employee";

  // Filter states
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [selectedAreaId, setSelectedAreaId] = useState<string>("");
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [customerName, setCustomerName] = useState("");
  const [paymentType, setPaymentType] = useState<string>("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");

  // Data states
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [summary, setSummary] = useState<PaymentReportSummary | null>(null);
  const [payments, setPayments] = useState<PaymentReportItem[]>([]);

  // Areas state
  const [areas, setAreas] = useState<{ id: number; name: string }[]>([]);
  const [loadingAreas, setLoadingAreas] = useState(false);

  // Search states
  const [customerSearch, setCustomerSearch] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");

  // Auto‑set employee filter for employees (they can also change it)
  useEffect(() => {
    if (isEmployee && user?.id) {
      setSelectedEmployeeId(user.id.toString());
    }
  }, [isEmployee, user]);

  // Load areas and employees on mount
  useEffect(() => {
    loadAreas();
    dispatch(fetchEmployees({ page: 1, limit: 100, search: "" }));
  }, []);

  const loadAreas = async () => {
    setLoadingAreas(true);
    try {
      const fetchedAreas = await locationApi.getAreas();
      setAreas(fetchedAreas);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingAreas(false);
    }
  };

  // Fetch customers when area changes
  useEffect(() => {
    if (selectedAreaId) {
      dispatch(
        fetchCustomers({
          page: 1,
          limit: 100,
          search: "",
          areaId: parseInt(selectedAreaId),
        }),
      );
      setSelectedCustomerId("");
      setCustomerName("");
    }
  }, [selectedAreaId]);

  // Debounced customer search (with area filter)
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (customerSearch !== undefined) {
        if (selectedAreaId !== "") {
          dispatch(
            fetchCustomers({
              page: 1,
              limit: 20,
              search: customerSearch,
              areaId: Number(selectedAreaId),
            }),
          );
        } else {
          dispatch(
            fetchCustomers({ page: 1, limit: 20, search: customerSearch }),
          );
        }
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [customerSearch, selectedAreaId]);

  // Debounced employee search
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (employeeSearch !== undefined) {
        dispatch(
          fetchEmployees({ page: 1, limit: 20, search: employeeSearch }),
        );
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [employeeSearch]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (selectedCustomerId) params.customerId = parseInt(selectedCustomerId);
      if (paymentType) params.paymentType = paymentType;
      if (selectedEmployeeId) params.createdBy = parseInt(selectedEmployeeId);
      const response = await reportsApi.getPaymentReport(params);
      setSummary(response.summary);
      setPayments(response.data);
    } catch (error: any) {
      showAlert("Error", error.message || "Failed to fetch report");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const params: any = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (selectedCustomerId) params.customerId = parseInt(selectedCustomerId);
      if (paymentType) params.paymentType = paymentType;
      if (selectedEmployeeId) params.createdBy = parseInt(selectedEmployeeId);
      const blob = await reportsApi.downloadPaymentReportPdf(params);

      if (Platform.OS === "web") {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } else {
        const reader = new FileReader();
        reader.onload = async () => {
          const base64 = (reader.result as string).split(",")[1];
          const fileUri =
            FileSystem.cacheDirectory + `payment_report_${Date.now()}.pdf`;
          await FileSystem.writeAsStringAsync(fileUri, base64, {
            encoding: FileSystem.EncodingType.Base64,
          });
          if (await Sharing.isAvailableAsync()) {
            await Sharing.shareAsync(fileUri);
          } else {
            showAlert("Error", "Sharing not available");
          }
        };
        reader.readAsDataURL(blob);
      }
    } catch (error: any) {
      showAlert("Error", error.message || "Failed to download report");
    } finally {
      setDownloading(false);
    }
  };

  const handleCustomerSelect = (value: string, label: string) => {
    setSelectedCustomerId(value);
    setCustomerName(label);
    setCustomerSearch("");
  };

  const handleClearCustomer = () => {
    setSelectedCustomerId("");
    setCustomerName("");
    setCustomerSearch("");
  };

  const handleEmployeeSelect = (value: string) => {
    setSelectedEmployeeId(value);
    setEmployeeSearch("");
  };

  const handleClearEmployee = () => {
    setSelectedEmployeeId("");
    setEmployeeSearch("");
  };

  const renderPaymentItem = ({ item }: { item: PaymentReportItem }) => (
    <View style={styles.paymentCard}>
      <View style={styles.paymentRow}>
        <Text style={styles.slNo}>#{item.slNo}</Text>
        <Text style={styles.customerName}>{item.customerName}</Text>
        <Text style={styles.amount}>₹{parseFloat(item.amount).toFixed(2)}</Text>
      </View>
      <View style={styles.paymentDetails}>
        <Text style={styles.typeText}>{item.paymentType.toUpperCase()}</Text>
        <Text style={styles.dateText}>
          {new Date(item.paymentDate).toLocaleDateString()}
        </Text>
        {item.referenceNo && (
          <Text style={styles.refText}>Ref: {item.referenceNo}</Text>
        )}
        <Text style={styles.createdByText}>By: {item.createdBy}</Text>
      </View>
    </View>
  );

  const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    keyboardAvoid: { flex: 1 },
    scrollContent: { padding: spacing.lg, paddingBottom: spacing.xl },
    title: {
      fontSize: 24,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.md,
    },
    backButton: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: spacing.md,
    },
    filterRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.sm,
      marginBottom: spacing.md,
      alignItems: "center",
    },
    filterButton: {
      flex: 1,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      padding: spacing.sm,
      alignItems: "center",
    },
    filterText: { fontSize: 14, color: colors.text },
    inputContainer: { marginBottom: spacing.md },
    buttonRow: {
      flexDirection: "row",
      gap: spacing.sm,
      marginBottom: spacing.lg,
    },
    summaryCard: {
      backgroundColor: colors.background,
      borderRadius: 12,
      padding: spacing.md,
      marginBottom: spacing.lg,
      borderWidth: 1,
      borderColor: colors.border,
    },
    summaryTitle: {
      fontSize: 16,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.sm,
    },
    summaryRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: spacing.xs,
    },
    summaryLabel: { fontSize: 14, color: colors.textLight },
    summaryValue: { fontSize: 14, fontWeight: "500", color: colors.text },
    grandValue: { fontSize: 16, fontWeight: "700", color: colors.primary },
    paymentCard: {
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: spacing.md,
      marginBottom: spacing.sm,
      borderWidth: 1,
      borderColor: colors.border,
    },
    paymentRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.xs,
    },
    slNo: { fontSize: 12, color: colors.textLight, marginRight: spacing.sm },
    customerName: {
      fontSize: 14,
      fontWeight: "600",
      color: colors.text,
      flex: 1,
    },
    amount: { fontSize: 14, fontWeight: "700", color: colors.primary },
    paymentDetails: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.sm,
      marginTop: spacing.xs,
    },
    typeText: {
      fontSize: 12,
      color: colors.textLight,
      backgroundColor: colors.border + "30",
      paddingHorizontal: spacing.xs,
      paddingVertical: 2,
      borderRadius: 4,
    },
    dateText: { fontSize: 12, color: colors.textLight },
    refText: { fontSize: 12, color: colors.textLight },
    createdByText: { fontSize: 12, color: colors.textLight },
    emptyText: {
      textAlign: "center",
      color: colors.textLight,
      marginTop: spacing.xxl,
    },
  });

  // Compute customer options with selected customer always included
  const getCustomerOptions = () => {
    const baseOptions = customers.map((c) => ({
      label: `${c.fullName} (${c.phone})${c.Area?.name ? ` - ${c.Area.name}` : ""}`,
      value: c.id.toString(),
    }));
    if (
      selectedCustomerId &&
      !baseOptions.some((opt) => opt.value === selectedCustomerId)
    ) {
      const selectedCust = customers.find(
        (c) => c.id.toString() === selectedCustomerId,
      );
      if (selectedCust) {
        baseOptions.unshift({
          label: `${selectedCust.fullName} (${selectedCust.phone})${selectedCust.Area?.name ? ` - ${selectedCust.Area.name}` : ""}`,
          value: selectedCust.id.toString(),
        });
      } else if (customerName) {
        baseOptions.unshift({
          label: customerName,
          value: selectedCustomerId,
        });
      }
    }
    return baseOptions;
  };

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.keyboardAvoid}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => router.back()}
          >
            <Ionicons name="arrow-back" size={24} color={colors.primary} />
            <Text style={{ color: colors.primary, marginLeft: 4 }}>Back</Text>
          </TouchableOpacity>

          <Text style={styles.title}>Payment Report</Text>

          <View style={styles.filterRow}>
            <DatePicker
              value={startDate}
              onChange={setStartDate}
              placeholder="Start Date"
              containerStyle={{ flex: 1, marginBottom: 0 }}
            />
            <DatePicker
              value={endDate}
              onChange={setEndDate}
              placeholder="End Date"
              containerStyle={{ flex: 1, marginBottom: 0 }}
            />
            {(startDate || endDate) && (
              <TouchableOpacity
                style={[styles.filterButton, { borderColor: colors.danger }]}
                onPress={() => {
                  setStartDate("");
                  setEndDate("");
                }}
              >
                <Text style={{ color: colors.danger }}>Clear</Text>
              </TouchableOpacity>
            )}
          </View>

          <SearchableSelect
            label="Area / Route"
            placeholder="All areas"
            options={areas.map((a) => ({
              label: a.name,
              value: a.id.toString(),
            }))}
            value={selectedAreaId}
            onValueChange={setSelectedAreaId}
            loading={loadingAreas}
            containerStyle={styles.inputContainer}
            clearable
          />

          <SearchableSelect
            label="Customer"
            placeholder="All customers"
            options={getCustomerOptions()}
            value={selectedCustomerId}
            onValueChange={handleCustomerSelect}
            onSearch={setCustomerSearch}
            loading={customersLoading}
            containerStyle={styles.inputContainer}
            clearable
            disabled={!selectedAreaId}
          />

          <SearchableSelect
            label="Payment Type"
            placeholder="All types"
            options={[
              { label: "Cash", value: "cash" },
              { label: "Bank", value: "bank" },
              { label: "Damage", value: "damage" },
            ]}
            value={paymentType}
            onValueChange={setPaymentType}
            containerStyle={styles.inputContainer}
            clearable
          />

          <SearchableSelect
            label="Created By (Employee)"
            placeholder="All employees"
            options={employees.map((emp) => ({
              label: `${emp.ownerName} ${emp.email ? `(${emp.email})` : ""}`,
              value: emp.id.toString(),
            }))}
            value={selectedEmployeeId}
            onValueChange={handleEmployeeSelect}
            onSearch={setEmployeeSearch}
            loading={employeesLoading}
            containerStyle={styles.inputContainer}
            clearable
          />

          <View style={styles.buttonRow}>
            <Button
              title="Apply Filters"
              onPress={fetchReport}
              loading={loading}
              variant="primary"
            />
            <Button
              title="Download PDF"
              onPress={handleDownloadPdf}
              loading={downloading}
              variant="outline"
            />
          </View>

          {summary && (
            <View style={styles.summaryCard}>
              <Text style={styles.summaryTitle}>Summary</Text>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Total Payments:</Text>
                <Text style={styles.summaryValue}>{summary.totalPayments}</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Total Amount:</Text>
                <Text style={[styles.summaryValue, styles.grandValue]}>
                  ₹{parseFloat(summary.totalAmount).toFixed(2)}
                </Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Cash:</Text>
                <Text style={styles.summaryValue}>
                  ₹{parseFloat(summary.totalCash).toFixed(2)}
                </Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Bank:</Text>
                <Text style={styles.summaryValue}>
                  ₹{parseFloat(summary.totalBank).toFixed(2)}
                </Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Damage:</Text>
                <Text style={styles.summaryValue}>
                  ₹{parseFloat(summary.totalDamage).toFixed(2)}
                </Text>
              </View>
            </View>
          )}

          <FlatList
            data={payments}
            keyExtractor={(item, index) => `${item.slNo}-${index}`}
            renderItem={renderPaymentItem}
            scrollEnabled={false}
            ListEmptyComponent={
              !loading && payments.length === 0 ? (
                <Text style={styles.emptyText}>
                  No payments found. Apply filters and click "Apply Filters".
                </Text>
              ) : null
            }
          />
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}
